import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IPsychosocialRiskResults } from '../services/reports';
import { THEME as theme } from '../styles/theme';

type TChartColors = Record<string, string[]>;

type TRiskNames = Record<string, string>;

interface IChartData {
  name: string;
  value: number;
}

interface IChart {
  data: IChartData[];
  colors: string[];
}

export interface IRiskAnalysisResults extends IPsychosocialRiskResults {
  chart: IChart;
  name: string;
}

const colors: TChartColors = {
  low: [
    theme.colors.accent.red[30],
    theme.colors.accent.orange[30],
    theme.colors.accent.green[100],
  ],
  high: [
    theme.colors.accent.red[100],
    theme.colors.accent.orange[30],
    theme.colors.accent.green[30],
  ],
  potential: [
    theme.colors.accent.red[30],
    theme.colors.accent.orange[100],
    theme.colors.accent.green[30],
  ],
};

const useRiskAnalysis = (results: IPsychosocialRiskResults[]) => {
  const { t } = useTranslation();

  const chart = useMemo<IChartData[]>(
    () =>
      results.map(({ result, risk }) => ({
        name: `${risk?.level}`,
        value: result.share,
      })),
    [results]
  );

  const riskTransformedData = useMemo<IRiskAnalysisResults[]>(() => {
    const names: TRiskNames = {
      low: t('report.riskAnalysis.lowRisk'),
      high: t('report.riskAnalysis.highRisk'),
      potential: t('report.riskAnalysis.potentialRisk'),
    };

    return results.map(({ risk, result }) => ({
      chart: { data: chart, colors: colors[risk.level] },
      name: names[risk.level],
      result: result,
      risk,
    }));
  }, [chart, results, t]);

  return { riskTransformedData };
};

export default useRiskAnalysis;
